import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useVariables } from '../../../../providers/VariableProvider'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import useReportComponentAccessors from '../inspector/useReportComponentAccessors'
import { buildPropAccessors } from '../inspector/buildPropAccessors'
import Select from '../../../molecules/Select'

function DDPSelect ({
  pipeline,
  variables,
  applyTo,
  defaultValue,
  seriesAccessor,
  valueAccessor,
  labelAccessor,
  defaultToFirst,
  hideIfEmpty,
  placeholder,
  variant,
  prefixLabel,
  fullWidth,
  size,
  className,
  style,
  labelClassName,
  labelStyle,
  readOnly,
  disabled
}) {
  const { setVariable, getVariable } = useVariables()
  const { data, isLoading, isFetching, error, inspectorId } = usePipelineDataContextWithVariables(pipeline, variables)

  const reportedAccessors = useMemo(
    () => buildPropAccessors({ valueAccessor, labelAccessor }),
    [valueAccessor, labelAccessor]
  )
  useReportComponentAccessors({
    inspectorId,
    component: 'DDPSelect',
    label: typeof pipeline === 'string' ? pipeline : null,
    accessors: reportedAccessors,
    dataAccessor: seriesAccessor
  })

  const options = useMemo(() => {
    if (!data) return []
    const items = seriesAccessor ? get(data, seriesAccessor) : data
    if (!Array.isArray(items)) return []
    return items.map(item => ({
      value: get(item, valueAccessor),
      label: String(get(item, labelAccessor) || get(item, valueAccessor) || '')
    }))
  }, [data, seriesAccessor, valueAccessor, labelAccessor])

  // Initialize variable when unset
  useEffect(() => {
    if (getVariable(applyTo) !== undefined) return
    if (defaultToFirst && options.length > 0) {
      setVariable(applyTo, options[0].value)
    } else if (defaultValue !== undefined) {
      setVariable(applyTo, defaultValue)
    }
  }, [defaultToFirst, defaultValue, options, applyTo, setVariable, getVariable])

  // Reset when current selection is no longer in the options list (e.g. upstream variable changed)
  useEffect(() => {
    if (!options.length) return
    const current = getVariable(applyTo)
    if (current === undefined) return
    const stillValid = options.some(opt => opt.value === current)
    if (!stillValid && defaultToFirst) {
      setVariable(applyTo, options[0].value)
    }
  }, [options, applyTo, defaultToFirst, setVariable, getVariable])

  const handleChange = useCallback((value) => {
    setVariable(applyTo, value)
  }, [setVariable, applyTo])

  if (isLoading) return null
  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`DDPSelect[${applyTo}]: pipeline error`, error)
    }
    return null
  }
  if (hideIfEmpty && !options.length) return null

  if (options.length === 1 && defaultToFirst) {
    return (
      <div className={className} style={style}>
        <span className={labelClassName} style={labelStyle}>
          {prefixLabel ? `${prefixLabel} ` : ''}{options[0]?.label ?? placeholder}
        </span>
      </div>
    )
  }

  return (
    <div className={className} style={style}>
      <Select
        variant={variant}
        options={options}
        value={getVariable(applyTo, defaultValue)}
        onChange={handleChange}
        placeholder={placeholder}
        prefixLabel={prefixLabel}
        fullWidth={fullWidth}
        size={size}
        readOnly={readOnly}
        disabled={disabled}
        loadingOpts={isFetching}
      />
    </div>
  )
}

DDPSelect.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
  /** Substitution values for the pipeline */
  variables: PropTypes.object,
  /** Variable key to write the selected value to */
  applyTo: PropTypes.string.isRequired,
  /** Explicit default value when defaultToFirst is false */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Accessor path to the options array in the pipeline result */
  seriesAccessor: PropTypes.string,
  /** Accessor path to the value field within each option */
  valueAccessor: PropTypes.string,
  /** Accessor path to the label field within each option */
  labelAccessor: PropTypes.string,
  /** Auto-select the first option on load */
  defaultToFirst: PropTypes.bool,
  /** Hide the component when pipeline returns no options */
  hideIfEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined', 'outlined-rounded']),
  prefixLabel: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  /** Class name for the single-item text label (when only one option exists) */
  labelClassName: PropTypes.string,
  /** Inline style for the single-item text label */
  labelStyle: PropTypes.object,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool
}

DDPSelect.defaultProps = {
  seriesAccessor: 'data',
  valueAccessor: 'id',
  labelAccessor: 'name',
  defaultToFirst: true,
  hideIfEmpty: false,
  placeholder: 'Select...',
  variant: 'outlined-rounded',
  fullWidth: true,
  labelClassName: 'font-size-sm font-weight-semibold'
}

export default DDPSelect
